import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTutorial } from '../../contexts/TutorialContext';
import { useLists } from '../../contexts/ListsContext';
import { useNotificationModal } from '../../contexts/NotificationModalContext';
import './TutorialPrompt.css';

/**
 * TutorialPrompt Component
 *
 * Shows a welcome prompt to users asking if they want to take the tutorial.
 * Only appears once per user (until completed or skipped).
 */
function TutorialPrompt() {
  const { user } = useAuth();
  const { lists, loading: listsLoading } = useLists();
  const { shouldShowTutorial, startTutorial, endTutorial, isActive } = useTutorial();
  const { showAlert } = useNotificationModal();
  const [showPrompt, setShowPrompt] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Only show prompt if:
    // 1. User is logged in
    // 2. Lists have loaded
    // 3. Tutorial hasn't been completed/skipped (in this browser)
    // 4. Tutorial isn't currently active
    // 5. User has no existing lists (prevents showing on new browser/device for existing users)
    const hasNoLists = !lists || lists.length === 0;
    if (user && !listsLoading && shouldShowTutorial() && !isActive && hasNoLists) {
      // Small delay before showing to let the page settle
      const timer = setTimeout(() => {
        setShowPrompt(true);
        // Trigger animation
        requestAnimationFrame(() => {
          setIsVisible(true);
        });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [user, lists, listsLoading, shouldShowTutorial, isActive]);

  const handleStartTutorial = () => {
    setIsVisible(false);
    setTimeout(() => {
      setShowPrompt(false);
      startTutorial();
    }, 300);
  };

  const handleSkip = async () => {
    setIsVisible(false);
    setTimeout(async () => {
      setShowPrompt(false);
      endTutorial(false); // Mark as skipped
      await showAlert('You can restart the tutorial anytime from Settings.', {
        title: 'Tutorial Skipped',
      });
    }, 300);
  };

  if (!showPrompt) {
    return null;
  }

  return (
    <div className={`tutorial-prompt-overlay ${isVisible ? 'visible' : ''}`}>
      <div className="tutorial-prompt-card">
        <div className="tutorial-prompt-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
          </svg>
        </div>

        <h2 className="tutorial-prompt-title">Welcome to Vibary!</h2>

        <p className="tutorial-prompt-description">
          Would you like a quick tour? We'll show you how to create lists, save places, connect with friends, and more.
        </p>

        <div className="tutorial-prompt-features">
          <div className="tutorial-prompt-feature">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <rect x="3" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="3" width="7" height="7" rx="1" />
              <rect x="3" y="14" width="7" height="7" rx="1" />
              <rect x="14" y="14" width="7" height="7" rx="1" />
            </svg>
            <span>Create lists</span>
          </div>
          <div className="tutorial-prompt-feature">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            <span>Save places</span>
          </div>
          <div className="tutorial-prompt-feature">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            <span>Connect with friends</span>
          </div>
        </div>

        <div className="tutorial-prompt-buttons">
          <button className="tutorial-prompt-btn tutorial-prompt-btn-secondary" onClick={handleSkip}>
            Skip for now
          </button>
          <button className="tutorial-prompt-btn tutorial-prompt-btn-primary" onClick={handleStartTutorial}>
            Take the tour
          </button>
        </div>

        <p className="tutorial-prompt-hint">
          Takes about 2 minutes
        </p>
      </div>
    </div>
  );
}

export default TutorialPrompt;
